const HERO_IMAGE =
  'https://images.unsplash.com/photo-1574629810360-7efbbe195018?auto=format&fit=crop&w=1600&q=80';

const RELATED_GUIDES = [
  {
    slug: 'soleus-pain-kt-tape-soccer-guide',
    title: 'Soleus Pain in Soccer: How to Use KT Tape & Keep Playing Safely',
    blurb: 'KT Tape application and modified return-to-play for lower-calf strains.',
  },
  {
    slug: '7v7-soccer-formation-over-35-guide',
    title: '7v7 Soccer Formation Guide for Players 35+',
    blurb: 'pace management, recovery, and injury-aware tactics.',
  },
  {
    slug: 'soccer-defender-drills-free-youtube-videos',
    title: 'Soccer Defender Drills + Free YouTube Training Videos',
    blurb: 'position training with recovery in mind.',
  },
  {
    slug: 'ohio-indian-health-wellness-guide',
    title: 'Ohio Indian Health & Wellness Guide (All Ages)',
    blurb: 'strength and conditioning plans for adult soccer players.',
  },
];

const REFERENCE_LINKS = [
  {
    label: 'FIFA 11+ injury prevention warm-up',
    url: 'https://inside.fifa.com/health-and-medical/injury-prevention',
  },
  {
    label: 'American Academy of Orthopaedic Surgeons — muscle strains',
    url: 'https://orthoinfo.aaos.org/en/diseases--conditions/muscle-strains/',
  },
  {
    label: 'British Journal of Sports Medicine — POLICE principle',
    url: 'https://bjsm.bmj.com/content/53/6/327',
  },
];

/** On-field remedies for calf, soleus/Achilles, and hamstring injuries in soccer */
export const SOCCER_FIELD_INJURY_REMEDIES_POST = {
  id: 'featured-soccer-field-injury-remedies',
  slug: 'soccer-field-injury-remedies-calf-hamstring',
  title: 'On-Field Remedies for Soccer Calf, Soleus & Hamstring Injuries',
  excerpt:
    'What to do immediately when a calf, lower-calf (soleus/Achilles), or hamstring goes on the pitch—first aid, what not to do, and when to stop playing. Not medical advice.',
  publishedAt: '2026-07-28T12:00:00.000Z',
  coverImageUrl: HERO_IMAGE,
  authorName: 'Daily Sports Life',
  clubName: 'Player Wellness',
  blogCategory: 'exercise',
  featured: true,
  component: 'WellnessGuideBlogArticle',
  theme: 'exercise',
  relatedPostsTitle: 'Related training & wellness guides',
  relatedPosts: RELATED_GUIDES,
  referenceLinks: REFERENCE_LINKS,
  sections: [
    {
      type: 'stats',
      ariaLabel: 'Guide overview',
      items: [
        { value: '3 injuries', label: 'Calf · Soleus · Hamstring' },
        { value: '0–15 min', label: 'On-field first aid window' },
        { value: 'Stop play', label: 'If you feel a pop or sharp tear' },
        { value: 'See a clinician', label: 'Before returning to sprinting' },
      ],
    },
    {
      type: 'panel',
      title: 'Why these injuries happen on the soccer field',
      paragraphs: [
        'Calf, lower-calf (soleus and Achilles area), and hamstring strains are among the most common **non-contact** injuries in adult recreational soccer. They usually strike during a sprint, sudden acceleration, overstretch when reaching for a ball, or a quick direction change on tired legs.',
        'The difference between missing one game and missing a season is often what you do in the **first 15 minutes**. This guide covers on-field first aid and immediate remedies—not a full rehab program. Always follow advice from a licensed sports medicine clinician who examines you in person.',
      ],
      chips: ['Rec & club soccer', 'Ohio leagues', 'First aid on pitch', 'Not medical advice'],
    },
    {
      type: 'table',
      title: 'Know what you injured — quick field check',
      intro: 'You cannot diagnose a grade on grass, but location and mechanism help you choose the right immediate response.',
      headers: ['Injury', 'Where it hurts', 'How it usually happens', 'On-field red flags'],
      rows: [
        [
          'Calf (gastrocnemius)',
          'Upper back of lower leg — the big “meat” of the calf',
          'Sudden sprint, jump, or push-off; may feel a pop',
          'Bruising, can’t walk without limping, visible swelling within an hour',
        ],
        [
          'Lower calf / soleus',
          'Deeper ache below the gastroc, often closer to the Achilles',
          'Fatigue after long match, slow jog, or repeated small sprints',
          'Pain when standing on tiptoe; hurts more walking than sprinting at first',
        ],
        [
          'Achilles tendon (below calf)',
          'Tight band just above the heel — not the muscle belly',
          'Sudden stop-start, hill sprints, or returning too fast after calf tightness',
          'Sharp pain at heel cord, audible snap, or gap you can feel — **stop immediately**',
        ],
        [
          'Hamstring',
          'Back of thigh — often mid-belly or near sit bone',
          'Sprint, overstretch reaching for ball, or deceleration',
          'Bruising down back of leg, pain when bending knee against resistance',
        ],
      ],
    },
    {
      type: 'panel',
      title: 'Step 1 — Stop playing right away',
      paragraphs: [
        'If you feel a **pop**, **snap**, or sudden sharp tear, come off the field. Do not “test it” with another sprint. Walking it off turns a Grade I strain into a Grade II or III.',
        'Signal to your captain or coach. Sit or lie down on the sideline with the leg supported. Remove cleats if swelling is starting — tight boots restrict circulation.',
      ],
      callout:
        'Rule of thumb: if you cannot walk four steps without a noticeable limp, you are done for the day—no exceptions.',
    },
    {
      type: 'panel',
      title: 'Step 2 — On-field POLICE protocol (first 15 minutes)',
      intro: 'Sports medicine has moved beyond old “RICE only” advice. On the sideline, think **POLICE**: Protection, Optimal loading, Ice, Compression, Elevation.',
      orderedList: [
        '**Protection** — Keep weight off the leg. Use crutches from the team bag if available. No stretching the injured muscle in the acute phase.',
        '**Optimal loading** — Gentle pain-free ankle pumps (point and flex) only if they do not increase pain. No jogging to “loosen it up.”',
        '**Ice** — 15–20 minutes with a barrier (towel) between ice and skin. Re-apply every 2 hours for the first day if swelling continues.',
        '**Compression** — Elastic bandage from foot toward knee, snug but not numb. Loosen if toes tingle or turn pale.',
        '**Elevation** — Leg above heart level on a bench or bag stack for 20–30 minutes while you arrange a ride home.',
      ],
    },
    {
      type: 'panel',
      title: 'Calf muscle (gastrocnemius) — on-field remedies',
      list: [
        'Sit with knee **straight** and calf on ice — the gastroc crosses the knee joint.',
        'Gentle compression wrap from ankle to just below knee.',
        'Avoid calf stretches and heel raises on day one — they pull on torn fibers.',
        'Take weight off: hop on one leg only if pain is mild; otherwise use two crutches.',
        'Mark the spot of maximum tenderness with a pen — helps your physio track swelling.',
        'Do not massage deeply into a fresh tear — it can increase bleeding.',
      ],
      callout:
        'Gastrocnemius tears often bruise within 24–48 hours. A large bruise or inability to push off means you need imaging before you play again.',
    },
    {
      type: 'panel',
      title: 'Lower calf / soleus & Achilles area — on-field remedies',
      paragraphs: [
        'The **soleus** sits deeper, under the main calf muscle. It hurts lower on the leg and is common in players who play two games in a weekend or skip warm-ups on cold Ohio mornings.',
        'The **Achilles tendon** is not muscle — it connects calf to heel. Tendon pain needs extra caution because rushing back raises rupture risk.',
      ],
      list: [
        'For soleus: ice with knee **slightly bent** (about 20–30°) — the soleus works more with bent knee.',
        'For Achilles pain: ice the tendon, keep ankle in neutral (not fully pointed), and **do not** do aggressive calf stretches on the field.',
        'Check the Thompson squeeze test only if trained — a clinician should evaluate suspected Achilles rupture.',
        'Wear a shoe with a slight heel lift (even your other cleat with an insole) to reduce stretch on the tendon while walking off.',
        'Soleus strains often feel “okay” at slow jog but fail on sprint — treat any lower-calf sharp pain as a full stop for the match.',
      ],
    },
    {
      type: 'panel',
      title: 'Hamstring — on-field remedies',
      list: [
        'Lie on your back, knee slightly bent, heel on ground — reduces tension on the hamstring.',
        'Ice the back of the thigh, not the front of the knee.',
        'Do not attempt forward folds or toe-touch stretches — they lengthen torn fibers.',
        'If pain is near the sit bone (ischial tuberosity), note it — high hamstring issues need longer rehab.',
        'Use crutches if every step causes sharp pain in the back of the thigh.',
        'Avoid sitting in low car seats on the drive home — slide the seat back and keep knee slightly bent.',
      ],
      callout:
        'Hamstrings re-injure easily. Players who return in under two weeks without rehab have the highest recurrence rate in recreational soccer.',
    },
    {
      type: 'table',
      title: 'What NOT to do on the field',
      headers: ['Mistake', 'Why it makes things worse'],
      rows: [
        ['“Run it off” with another sprint', 'Turns partial tears into full tears; hides severity until you cannot walk'],
        ['Deep massage or “working out the knot”', 'Increases bleeding and inflammation in the first 48 hours'],
        ['Aggressive static stretching', 'Pulls on injured muscle fibers before they have scarred'],
        ['Heat rub or hot pack immediately', 'Heat increases blood flow and swelling in acute injuries'],
        ['Playing the second half “because we need subs”', 'A 6-week injury becomes a 6-month problem'],
        ['Anti-inflammatory meds to “get through the game”', 'Masks pain so you load a damaged tissue past safe limits'],
      ],
    },
    {
      type: 'panel',
      title: 'Step 3 — First 24–72 hours after you leave the field',
      orderedList: [
        'Rest the leg — no soccer, no pickup, no “light jog to see how it feels.”',
        'Continue ice 15–20 min, 3–5 times per day for the first 48 hours if swelling persists.',
        'Keep compression during the day; remove at night unless your clinician says otherwise.',
        'Sleep with leg elevated on a pillow.',
        'Book a sports physio or orthopaedic visit within the first week — sooner for Achilles or complete inability to bear weight.',
        'Use acetaminophen for pain if appropriate for you; ask your doctor before NSAIDs in the first 24 hours if bleeding/bruising is significant.',
      ],
    },
    {
      type: 'panel',
      title: 'When to seek urgent care (same day)',
      list: [
        'Heard or felt a pop in the Achilles and cannot rise onto tiptoe on the injured side.',
        'Severe swelling within an hour, or leg looks deformed.',
        'Numbness, tingling, or foot turning cold or pale after compression.',
        'Cannot bear any weight after 30 minutes of rest, ice, and elevation.',
        'Fever with redness spreading on the leg (possible infection — rare but serious).',
        'Hamstring pain with sudden loss of strength and large bruise within hours.',
      ],
    },
    {
      type: 'panel',
      title: 'Return to play — general milestones (after clinician clearance)',
      intro: 'Timelines vary by grade. These are typical checkpoints a physio uses — not a DIY schedule.',
      list: [
        'Pain-free walking and stairs for several days.',
        'Full range of motion equal to the other leg without sharp pain.',
        'Strength at least 90% of the uninjured side (often tested with single-leg calf raise or hamstring curl).',
        'Pain-free jog, then progressive sprint and change-of-direction drills.',
        'Sport-specific session: passing, shooting, small-sided games before full match minutes.',
        'For hamstrings: Nordic hamstring strength and single-leg bridge symmetry before competitive sprints.',
        'For calves/Achilles: 25+ single-leg calf raises with slow lowering, and hop tests without pain.',
      ],
    },
    {
      type: 'panel',
      title: 'Prevention — before the next match',
      list: [
        'Run the **FIFA 11+** warm-up (or equivalent) every session — cuts lower-limb injury rates in amateur players.',
        'Do not skip the jog and dynamic stretch on cold or wet Ohio evenings.',
        'Build calf endurance: seated and standing calf raises 2–3× per week in season.',
        'Add Nordic hamstring curls or partner-assisted eccentrics 1–2× per week.',
        'Manage minutes if you played a full 90 on Saturday and have a Sunday league game.',
        'Hydrate and replace electrolytes in summer — cramping precedes many calf and hamstring tears.',
        'Replace cleats when studs are worn — poor grip forces compensatory sprint mechanics.',
      ],
    },
    {
      type: 'panel',
      title: 'Sideline kit every team should carry',
      list: [
        'Instant cold packs (or ice in a cooler for home fields).',
        'Elastic bandages (3-inch and 4-inch).',
        'Athletic tape and pre-wrap.',
        'Crutches or a knee scooter for adult leagues with aging rosters.',
        'Emergency contact list and nearest urgent care address for away games.',
        'A printed copy of this guide in the team bag — someone will need it at 8:47 PM on a Thursday.',
      ],
      callout:
        'Coaches: pull a player at the first report of sharp posterior leg pain. Applause for toughness is cheaper than losing a starter for two months.',
    },
  ],
};
